import type { DirectPaymentAttachment } from "Components/Chat/chatAttachmentTypes";
import { Box, Link, Stack, Typography } from "@mui/material";
import { SendOutlined } from "@mui/icons-material";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";
import { formatCurrency } from "Utils/CurrencyUtils";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useTransactionPolling } from "Hooks/Chat/useTransactionPolling";
import { Resources, useResource } from "Translations/Resources";
import {
  statusVariants,
  defaultVariant,
} from "Components/Chat/transactionStatusUtils";

const DIRECT_PAYMENT_RESOURCES = Resources.Chat.DirectPayment;

type DirectPaymentCardProps = {
  attachment: DirectPaymentAttachment;
};

export const DirectPaymentCard = ({
  attachment: { transactionPublicID, amount, currencyCode, transactionStatus },
}: DirectPaymentCardProps) => {
  const navigate = useNavigate();
  const { t } = useResource();
  const user = useLoggedInUser();

  const publicID = transactionPublicID;
  const { data } = useTransactionPolling(publicID);

  const status = data?.transactionStatus ?? transactionStatus;
  const isCurrentUserDebtor = user?.userID === data?.debtorID;
  const displayName = data?.creditorName;
  const { cardBackground, statusBackground, icon, statusColor, labelKey } =
    statusVariants[status] ?? defaultVariant;

  const formattedAmount = formatCurrency({ amount, currencyCode });

  return (
    <Box
      className="direct-payment-card"
      width="100%"
      minWidth={250}
      maxWidth={350}
      borderRadius={3}
      overflow="hidden"
      p={2}
      sx={{ backgroundColor: cardBackground }}
    >
      <Stack gap={1} p={2} pt={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <SendOutlined fontSize="small" color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            {isCurrentUserDebtor
              ? t(DIRECT_PAYMENT_RESOURCES.Sent)
              : t(DIRECT_PAYMENT_RESOURCES.Received)}
          </Typography>
        </Stack>
        {displayName && (
          <Typography variant="subtitle1" fontWeight="bold">
            {displayName}
          </Typography>
        )}
        <Link
          component="button"
          variant="body2"
          color="text.secondary"
          textAlign="left"
          width="fit-content"
          onClick={() =>
            navigate(`${appUrl("transaction-detail")}/${publicID}`)
          }
        >
          ID: {publicID}
        </Link>
        <Typography
          variant="h4"
          fontWeight="bold"
          textAlign="center"
          mt={2}
          mb={1}
        >
          {formattedAmount}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={1.5}
        mx={1}
        mb={1}
        py={1.5}
        px={2.5}
        borderRadius={3}
        sx={{ backgroundColor: statusBackground }}
      >
        <Box
          width={25}
          height={25}
          borderRadius="50%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ backgroundColor: "rgba(255,255,255,0.3)" }}
        >
          {icon}
        </Box>
        <Typography fontWeight={600} fontSize={18} color={statusColor}>
          {labelKey(t)}
        </Typography>
      </Stack>
    </Box>
  );
};
